// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '../../store/AppContext.js';
import { formatAmount, INCOME_TYPES, EXPENSE_TYPES } from '../../lib/eventLog.js';
import { formatDate } from '../../lib/formatLocale.js';
import type { Budget } from '../../types.js';

export function BudgetReport() {
  const { t } = useTranslation(['reports', 'common']);
  const { state } = useApp();
  const { ledger } = state;
  const [searchParams] = useSearchParams();

  const budgets = [...ledger.budgets.values()].sort((a, b) => a.name.localeCompare(b.name));
  const initialId = searchParams.get('budgetId') ?? budgets[0]?.id ?? '';
  const [selectedId, setSelectedId] = useState(initialId);

  const budget: Budget | undefined = ledger.budgets.get(selectedId);

  const { rows, periods } = useMemo(() => {
    if (!budget) return { rows: [], periods: [] };
    const b = budget;

    const periods = Array.from({ length: b.numPeriods }, (_, i) => i + 1);

    function getPeriodDateRange(period: number): [string, string] {
      const startYear = parseInt(b.startDate.slice(0, 4), 10);
      const startMonth = parseInt(b.startDate.slice(5, 7), 10) - 1;

      function localDate(y: number, m: number, day: number): string {
        return `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }

      if (b.periodType === 'monthly') {
        const d = new Date(startYear, startMonth + period - 1, 1);
        const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        return [localDate(d.getFullYear(), d.getMonth(), 1), localDate(d.getFullYear(), d.getMonth(), lastDay)];
      }
      if (b.periodType === 'quarterly') {
        const offsetMonths = startMonth + (period - 1) * 3;
        const pStartYear = startYear + Math.floor(offsetMonths / 12);
        const pStartMonth = offsetMonths % 12;
        const lastDay = new Date(pStartYear, pStartMonth + 3, 0).getDate();
        const endMonth = (pStartMonth + 2) % 12;
        const endYear = pStartYear + Math.floor((pStartMonth + 2) / 12);
        return [localDate(pStartYear, pStartMonth, 1), localDate(endYear, endMonth, lastDay)];
      }
      const pStartYear = startYear + (period - 1);
      const lastDay = new Date(pStartYear, startMonth + 12, 0).getDate();
      const endMonth = (startMonth + 11) % 12;
      const endYear = pStartYear + Math.floor((startMonth + 11) / 12);
      return [localDate(pStartYear, startMonth, 1), localDate(endYear, endMonth, lastDay)];
    }

    const accounts = [...ledger.accounts.values()].filter(
      (a) => !a.placeholder && (INCOME_TYPES.has(a.type) || EXPENSE_TYPES.has(a.type)),
    );

    const budgetMap = new Map<string, Map<number, number>>();
    for (const v of b.values) {
      if (!budgetMap.has(v.accountId)) budgetMap.set(v.accountId, new Map());
      budgetMap.get(v.accountId)!.set(v.period, v.amount);
    }

    const actualMap = new Map<string, Map<number, number>>();
    for (const period of periods) {
      const [start, end] = getPeriodDateRange(period);
      for (const tx of ledger.transactions.values()) {
        if (tx.voided) continue;
        if (tx.date < start || tx.date > end) continue;
        for (const split of tx.splits) {
          if (!actualMap.has(split.accountId)) actualMap.set(split.accountId, new Map());
          const pMap = actualMap.get(split.accountId)!;
          pMap.set(period, (pMap.get(period) ?? 0) + Math.abs(split.amount));
        }
      }
    }

    const rows = accounts.map((account) => {
      const bMap = budgetMap.get(account.id) ?? new Map();
      const aMap = actualMap.get(account.id) ?? new Map();
      return {
        account,
        periods: periods.map((p) => ({
          period: p,
          budgeted: bMap.get(p) ?? 0,
          actual: aMap.get(p) ?? 0,
        })),
      };
    });

    return { rows, periods };
  }, [budget, ledger.transactions, ledger.accounts]);

  function getPeriodLabel(period: number): string {
    if (!budget) return t('reports:budgetPeriods.period', { n: period });
    if (budget.periodType === 'monthly') {
      const sy = parseInt(budget.startDate.slice(0, 4), 10);
      const sm = parseInt(budget.startDate.slice(5, 7), 10) - 1;
      const d = new Date(sy, sm + period - 1, 1);
      return formatDate(d, { month: 'short', year: '2-digit' });
    }
    if (budget.periodType === 'quarterly') return t('reports:budgetPeriods.quarter', { n: period });
    return t('reports:budgetPeriods.year', { n: period });
  }

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex items-center gap-4 mb-4">
        <h2 className="text-md font-semibold text-text">{t('reports:titles.budgetReport')}</h2>
        <div className="flex items-center gap-2">
          <label className="text-xs text-text-muted">{t('reports:budgetReport.budget')}</label>
          <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)}
            className="bg-bg border border-border text-text text-sm px-2 py-1 focus:border-accent focus:outline-none">
            {budgets.length === 0 && <option value="">{t('reports:empty.noBudgets')}</option>}
            {budgets.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      </div>

      {!budget ? (
        <div className="text-center py-16 text-text-subtle">{t('reports:empty.noBudgetSelected')}</div>
      ) : (
        <div className="bg-bg-surface border border-border overflow-auto">
          <table className="text-sm">
            <thead className="border-b border-border-strong">
              <tr>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4 min-w-40">{t('reports:columns.account')}</th>
                {periods.map((p) => (
                  <th key={p} colSpan={3} className="text-center text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2 border-l border-border">
                    {getPeriodLabel(p)}
                  </th>
                ))}
              </tr>
              <tr className="border-b border-border">
                <th className="py-1 px-4" />
                {periods.map((p) => (
                  <React.Fragment key={p}>
                    <th className="text-right text-xs text-text-subtle py-1 px-2 border-l border-border">{t('reports:budgetReport.budget')}</th>
                    <th className="text-right text-xs text-text-subtle py-1 px-2">{t('reports:sections.actual')}</th>
                    <th className="text-right text-xs text-text-subtle py-1 px-2">{t('reports:sections.diff')}</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={1 + periods.length * 3} className="py-8 text-center text-text-subtle">{t('reports:empty.noExpenseIncomeAccounts')}</td></tr>
              )}
              {rows.map(({ account, periods: pData }) => (
                <tr key={account.id} className="border-b border-border hover:bg-bg-elevated">
                  <td className="py-1.5 px-4 text-text">{account.name}</td>
                  {pData.map(({ period, budgeted, actual }) => {
                    const diff = budgeted - actual;
                    const isExpense = EXPENSE_TYPES.has(account.type);
                    const isOver = isExpense ? actual > budgeted : actual < budgeted;
                    return (
                      <React.Fragment key={period}>
                        <td className="py-1.5 px-2 text-right font-mono text-text-subtle border-l border-border">
                          {budgeted > 0 ? formatAmount(budgeted) : '—'}
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono text-text">
                          {actual > 0 ? formatAmount(actual) : '—'}
                        </td>
                        <td className={`py-1.5 px-2 text-right font-mono text-xs ${diff === 0 ? 'text-text-subtle' : isOver ? 'text-negative font-semibold' : 'text-positive'}`}>
                          {budgeted > 0 || actual > 0 ? (diff >= 0 ? '+' : '') + formatAmount(diff) : '—'}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
